import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

/**
 * WordMorphology — the word-structure "blow-up" modal (DESIGN.md §9).
 *
 * Takes a single `word` prop shaped exactly as §9:
 *   { label, segments: [{ type, text, labelAr, labelEn, side, hasCase? }], irab }
 *
 * Visuals/motion mirror design/reference/full-app-dark-theme.html: color-coded
 * segments (prefix→rose, root→gold, pronoun→teal), a pulsing violet case dot,
 * SVG bezier connectors from each segment to its label card, the إعراب callout,
 * a staggered reveal, and a legend. Connectors are (re)drawn on mount and on
 * window resize; the resize listener is cleaned up on unmount.
 */

const SEG_COLOR = { prefix: "var(--rose)", root: "var(--gold)", pronoun: "var(--teal)" };
const CASE_COLOR = "var(--violet)";

function prefersReducedMotion() {
  return typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export default function WordMorphology({ word, onClose, verifiable = false, verified = false, onVerify }) {
  const segments = word?.segments || [];
  const irab = word?.irab || {};
  const caseIndex = segments.findIndex((s) => s.hasCase);

  const diagramRef = useRef(null);
  const wordStageRef = useRef(null);
  const irabRef = useRef(null);
  const caseDotRef = useRef(null);
  const segRefs = useRef({});   // segment index -> element
  const labelRefs = useRef({}); // segment index -> label-card element
  const timers = useRef([]);

  const [show, setShow] = useState(false);          // backdrop/panel entrance
  const [expand, setExpand] = useState(false);      // word-stage blow-up
  const [shownLabels, setShownLabels] = useState(() => new Set());
  const [irabShown, setIrabShown] = useState(false);
  const [connectors, setConnectors] = useState([]); // {key, d, color, dashed}
  const [svgSize, setSvgSize] = useState({ w: 0, h: 0 });
  const [drawn, setDrawn] = useState(false);        // triggers stroke draw-in

  // ── Draw / redraw the SVG connector lines from current DOM geometry ──────
  const drawConnectors = useCallback(() => {
    const diagram = diagramRef.current;
    if (!diagram) return;
    const d = diagram.getBoundingClientRect();
    setSvgSize({ w: d.width, h: d.height });

    const lines = [];
    segments.forEach((seg, i) => {
      const segEl = segRefs.current[i];
      const cardEl = labelRefs.current[i];
      if (!segEl || !cardEl) return;
      const f = segEl.getBoundingClientRect();
      const t = cardEl.getBoundingClientRect();
      const fx = f.left + f.width / 2 - d.left;
      const fy = seg.side === "top" ? f.top - d.top : f.bottom - d.top;
      const tx = t.left + t.width / 2 - d.left;
      const ty = seg.side === "top" ? t.bottom - d.top : t.top - d.top;
      const midY = (fy + ty) / 2;
      lines.push({
        key: `seg-${i}`,
        d: `M ${fx} ${fy} C ${fx} ${midY}, ${tx} ${midY}, ${tx} ${ty}`,
        color: SEG_COLOR[seg.type] || "var(--gold)",
        dashed: false,
      });
    });

    // Case dot → إعراب callout (dashed, static).
    const dot = caseDotRef.current;
    const card = irabRef.current;
    if (dot && card) {
      const f = dot.getBoundingClientRect();
      const t = card.getBoundingClientRect();
      const fx = f.left + f.width / 2 - d.left;
      const fy = f.top - d.top;
      const tx = t.left + t.width / 2 - d.left;
      const ty = t.bottom - d.top;
      const midY = (fy + ty) / 2;
      lines.push({
        key: "case",
        d: `M ${fx} ${fy} C ${fx} ${midY}, ${tx} ${midY}, ${tx} ${ty}`,
        color: CASE_COLOR,
        dashed: true,
      });
    }
    setConnectors(lines);
  }, [segments]);

  // ── Entrance timeline + connector drawing (mount) ────────────────────────
  useEffect(() => {
    const reduce = prefersReducedMotion();
    const push = (fn, ms) => { timers.current.push(setTimeout(fn, ms)); };

    // Reveal order matches DOM order: top-row labels first, then bottom-row.
    const revealOrder = [
      ...segments.map((s, i) => (s.side === "top" ? i : -1)).filter((i) => i >= 0),
      ...segments.map((s, i) => (s.side !== "top" ? i : -1)).filter((i) => i >= 0),
    ];

    if (reduce) {
      setShow(true);
      setExpand(true);
      setShownLabels(new Set(segments.map((_, i) => i)));
      setIrabShown(true);
      setDrawn(true);
      push(drawConnectors, 0);
      return () => { timers.current.forEach(clearTimeout); timers.current = []; };
    }

    // Backdrop/panel entrance next frame so the transition plays.
    const raf = requestAnimationFrame(() => { setShow(true); setExpand(true); });

    push(() => {
      setIrabShown(true);
      revealOrder.forEach((segIdx, order) => {
        push(() => setShownLabels((prev) => new Set(prev).add(segIdx)), order * 130);
      });
      drawConnectors();
      requestAnimationFrame(() => setDrawn(true));
    }, 220);
    // Redraw as the expand / reveal transitions settle.
    push(drawConnectors, 600);
    push(drawConnectors, 850);

    return () => {
      cancelAnimationFrame(raf);
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawConnectors]);

  // ── Redraw on window resize (cleaned up on unmount) ──────────────────────
  useEffect(() => {
    const onResize = () => drawConnectors();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [drawConnectors]);

  // ── Escape to close + body scroll lock ───────────────────────────────────
  const handleClose = useCallback(() => {
    if (prefersReducedMotion()) { onClose && onClose(); return; }
    setShow(false);
    setTimeout(() => onClose && onClose(), 300);
  }, [onClose]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") handleClose(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [handleClose]);

  if (!word || segments.length === 0) return null;

  const renderLabel = (seg, i) => (
    <div
      key={`label-${i}`}
      ref={(el) => { labelRefs.current[i] = el; }}
      className={`wm-label-card${shownLabels.has(i) ? " wm-show" : ""}`}
    >
      <span className="wm-dot" style={{ background: SEG_COLOR[seg.type] || "var(--gold)" }} />
      <div className="wm-term-ar">{seg.labelAr}</div>
      <div className="wm-term-en">{seg.labelEn}</div>
    </div>
  );

  return ReactDOM.createPortal(
    <div
      className={`wm-backdrop${show ? " wm-show" : ""}`}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
      role="dialog"
      aria-modal="true"
    >
      <div className="wm-panel">
        <div className="wm-head">
          <p className="wm-eyebrow">{word.label}</p>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {verifiable && (
              <button
                className={`wm-verify${verified ? " wm-on" : ""}`}
                onClick={onVerify}
                title="Staff: mark this analysis reviewed-correct (gold example)"
              >
                ✓ {verified ? "Verified" : "Verify"}
              </button>
            )}
            <button className="wm-close" onClick={handleClose} aria-label="Close">✕</button>
          </div>
        </div>

        <div className="wm-diagram" ref={diagramRef}>
          <svg viewBox={`0 0 ${svgSize.w} ${svgSize.h}`} preserveAspectRatio="none">
            {connectors.map((c) => (
              <path
                key={c.key}
                className="wm-connector-line"
                d={c.d}
                pathLength={c.dashed ? undefined : 1}
                style={{
                  stroke: c.color,
                  ...(c.dashed
                    ? { strokeDasharray: "3,4" }
                    : {
                        strokeDasharray: 1,
                        strokeDashoffset: drawn ? 0 : 1,
                        transition: "stroke-dashoffset 0.55s ease",
                      }),
                }}
              />
            ))}
          </svg>

          <div
            ref={irabRef}
            className={`wm-irab-card${irabShown ? " wm-show" : ""}`}
          >
            <div className="wm-irab-eyebrow">Case · الإعراب</div>
            <div className="wm-status-ar">{irab.statusAr}</div>
            <div className="wm-status-en">{irab.statusEn}</div>
            {irab.reason && <div className="wm-reason">{irab.reason}</div>}
          </div>

          <div className="wm-labels-row wm-top">
            {segments.map((seg, i) => (seg.side === "top" ? renderLabel(seg, i) : null))}
          </div>

          <div className={`wm-word-stage${expand ? " wm-expand" : ""}`} ref={wordStageRef}>
            {segments.map((seg, i) => (
              <span
                key={`seg-${i}`}
                ref={(el) => { segRefs.current[i] = el; }}
                className={`wm-segment ${seg.type}`}
              >
                {seg.text}
                {i === caseIndex && (
                  <span ref={caseDotRef} className="wm-case-dot wm-pulse" />
                )}
              </span>
            ))}
          </div>

          <div className="wm-labels-row wm-bottom">
            {segments.map((seg, i) => (seg.side !== "top" ? renderLabel(seg, i) : null))}
          </div>
        </div>

        <div className="wm-legend">
          <div className="wm-legend-item prefix"><span className="wm-sw" />Prefix — سابقة</div>
          <div className="wm-legend-item root"><span className="wm-sw" />Root / Stem — جذر</div>
          <div className="wm-legend-item case"><span className="wm-sw" />Case ending — حركة الإعراب</div>
          <div className="wm-legend-item pronoun"><span className="wm-sw" />Attached pronoun — ضمير متصل</div>
        </div>
      </div>
    </div>,
    document.body
  );
}
